import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#ef4444", "#14b8a6"];

interface DeptItem {
  department: string;
  count: number;
}

interface ContractItem {
  contractType: string;
  count: number;
}

interface GenderItem {
  gender: string;
  count: number;
}

interface Props {
  deptData: DeptItem[];
  contractData: ContractItem[];
  genderData?: GenderItem[];
  isLoading?: boolean;
}

export function DepartmentBreakdown({ deptData, contractData, genderData = [], isLoading }: Props) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("analytics.departmentBreakdown")}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-72 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("analytics.departmentBreakdown")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="bar">
          <TabsList className="mb-4">
            <TabsTrigger value="bar">By Department</TabsTrigger>
            <TabsTrigger value="pie">By Contract Type</TabsTrigger>
            <TabsTrigger value="gender">{t("analytics.byGender")}</TabsTrigger>
          </TabsList>

          <TabsContent value="bar">
            {deptData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={deptData} margin={{ top: 5, right: 20, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="department"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                    angle={-35}
                    textAnchor="end"
                  />
                  <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} width={35} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: 13,
                    }}
                  />
                  <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Employees" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </TabsContent>

          <TabsContent value="pie">
            {contractData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={contractData}
                    dataKey="count"
                    nameKey="contractType"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ contractType, percent }) =>
                      `${contractType} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {contractData.map((_entry, idx) => (
                      <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: 13,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </TabsContent>

          <TabsContent value="gender">
            {genderData.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">{t("analytics.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={genderData}
                    dataKey="count"
                    nameKey="gender"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ gender, percent }) =>
                      `${t(`organization.demographics.genderLabels.${gender.toLowerCase()}`, gender)} ${(percent * 100).toFixed(0)}%`
                    }
                    labelLine={false}
                  >
                    {genderData.map((_entry, idx) => (
                      <Cell key={idx} fill={COLORS[(idx + 3) % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend formatter={(value: string) => t(`organization.demographics.genderLabels.${value.toLowerCase()}`, value)} />
                  <Tooltip
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "8px",
                      fontSize: 13,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
